import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/LanguageContext';

export default function OrderExportDialog({ initialDates, onClose, onExport, filtered = false }) {
  const t = useT();
  const dialog = useRef(null);
  const [range, setRange] = useState(initialDates);
  const [mode, setMode] = useState('custom');
  const [busy, setBusy] = useState(false);
  const valid =
    mode === 'all' || (range.dateFrom && range.dateTo && range.dateFrom <= range.dateTo);
  useEffect(() => {
    const node = dialog.current;
    node.showModal();
    return () => node.close();
  }, []);
  const download = async (event) => {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    try {
      if (await onExport(mode === 'all' ? {} : range)) onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <dialog
      ref={dialog}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      className="m-auto rounded-2xl border border-slate-200 p-6 shadow-xl w-[440px] max-w-[calc(100%-32px)] backdrop:bg-slate-900/30"
      aria-labelledby="order-export-title"
    >
      <form onSubmit={download}>
        <h2 id="order-export-title" className="text-lg font-semibold text-slate-900">
          {t('Export orders to Excel')}
        </h2>
        <p className="text-sm text-slate-500 mt-2 mb-5">
          {t(
            filtered
              ? 'Download orders matching the current portal, distributor, status and search filters during the selected dates. Both dates are included.'
              : 'Download all orders you can access, across all statuses, submitted during the selected dates. Both dates are included.'
          )}
        </p>
        <label className="block text-sm font-medium mb-2" htmlFor="order-export-period">
          {t('Date submitted')}
        </label>
        <select
          id="order-export-period"
          value={mode}
          disabled={busy}
          onChange={(e) => setMode(e.target.value)}
          className="w-full border border-slate-300 rounded-lg p-2 mb-4"
        >
          <option value="custom">{t('Custom date range')}</option>
          <option value="all">{t('All dates')}</option>
        </select>
        {mode === 'custom' && (
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm font-medium">
              {t('Start date')}
              <input
                type="date"
                required
                disabled={busy}
                value={range.dateFrom}
                max={range.dateTo || undefined}
                onChange={(e) => setRange({ ...range, dateFrom: e.target.value })}
                className="block w-full min-w-0 border border-slate-300 rounded-lg p-2 mt-1"
              />
            </label>
            <label className="text-sm font-medium">
              {t('End date')}
              <input
                type="date"
                required
                disabled={busy}
                value={range.dateTo}
                min={range.dateFrom || undefined}
                onChange={(e) => setRange({ ...range, dateTo: e.target.value })}
                className="block w-full min-w-0 border border-slate-300 rounded-lg p-2 mt-1"
              />
            </label>
          </div>
        )}
        {mode === 'custom' && range.dateFrom && range.dateTo && range.dateFrom > range.dateTo && (
          <p role="alert" className="text-sm text-red-700 mt-2">
            {t('End date must be on or after start date.')}
          </p>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="border border-slate-300 rounded-lg px-4 py-2 text-sm"
          >
            {t('Cancel')}
          </button>
          <button
            type="submit"
            disabled={!valid || busy}
            className="bg-[#2958e8] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {t(busy ? 'Downloading...' : 'Download Excel')}
          </button>
        </div>
      </form>
    </dialog>
  );
}
